import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Size, HR_COMPLAINTS_PATH, HR_COMPLAINTS_ROUTE, HR_COMPLAINT_ROUTE_CHECK_PATH, complaintSummaryProblem, t, type ComplaintsAnswer, type RaiseComplaintAnswer,
} from '@bms2/shared';
import { Button, Select, Textarea } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { post, useApi } from '../records/api';
import styles from './HrPages.module.css';

/**
 * Raise a complaint, from the signed-in person's own Person page (piece 34,
 * Complaints; decision 1468). The complaint is raised for the viewer's own
 * employment. 🔒 Choosing anybody who reads complaints, HR or Admin, as its subject shows the outside
 * route from Settings › HR in place of the words box, and nothing typed is
 * sent: the window then raises it with no words, once, and the system keeps
 * only that the route was offered and when.
 */
export function RaiseComplaintAction({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span data-record-action="raise-complaint">
      <Button size={Size.Small} text={[t('hr.complaint.raise')]} actions={[() => setOpen(true)]} />
      {open ? <RaiseComplaintModal apiBaseUrl={apiBaseUrl} onClose={() => setOpen(false)} /> : null}
    </span>
  );
}

function RaiseComplaintModal({ apiBaseUrl, onClose }: { apiBaseUrl: string; onClose: () => void }) {
  const fetched = useApi<ComplaintsAnswer>(HR_COMPLAINTS_PATH, apiBaseUrl);
  const [about, setAbout] = useState('');
  const [summary, setSummary] = useState('');
  const [route, setRoute] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [raised, setRaised] = useState(false);
  // 🔨 The route-offered row is written by the raise, once per window, never by the question asked as the subject is chosen.
  const offered = useRef(false);
  const subjects = fetched.state.kind === 'ready' ? fetched.state.data.subjects : [];
  const problem = complaintSummaryProblem(summary);

  const choose = async (value: string) => {
    setAbout(value);
    setRoute(null);
    setMessage(null);
    if (!value) return;
    // 🔒 Asked before a word is typed, so a complaint about anybody who reads complaints never leaves the browser (decision 2680).
    const answer = await post<RaiseComplaintAnswer | null>(HR_COMPLAINT_ROUTE_CHECK_PATH, { aboutEmploymentId: value }, apiBaseUrl);
    if (!answer.ok) return setMessage(answer.message);
    if (!answer.data || answer.data.kind !== 'RouteOffered') return;
    setRoute(answer.data.route);
    if (offered.current) return;
    offered.current = true;
    // No words are sent: the server routes it outside before it reads any, and records only that the route was offered.
    await post<RaiseComplaintAnswer>(HR_COMPLAINTS_PATH, { aboutEmploymentId: value, summary: '' }, apiBaseUrl);
  };
  const raise = async () => {
    const answer = await post<RaiseComplaintAnswer>(HR_COMPLAINTS_PATH, { aboutEmploymentId: about || null, summary }, apiBaseUrl);
    if (!answer.ok) return setMessage(answer.message);
    if (answer.data.kind === 'RouteOffered') return setRoute(answer.data.route);
    setRaised(true);
  };

  return (
    <Modal
      open
      title={t('hr.complaint.raiseTitle')}
      dataModal="raise-complaint"
      onCancel={onClose}
      actions={raised || route !== null
        // 🔒 Decision 2140: a complaint already sent writes nothing more, so the window carries one Close (decision 3063).
        ? [{ kind: 'close', label: t('hr.complaint.done'), onAct: onClose }]
        : [{ kind: 'primary', label: t('hr.complaint.raiseSubmit'), onAct: () => void raise(), disabled: problem !== null }, { kind: 'cancel', label: t('modal.cancel'), onAct: onClose }]}
    >
      <div className={styles.fields}>
        {raised ? (
          <p data-complaint-raised="true">{t('hr.complaint.raised')} <Link to={HR_COMPLAINTS_ROUTE}>{t('hr.complaint.yours')}</Link></p>
        ) : (
          <>
            <p className={styles.meta}>{t('hr.complaint.raiseHint')}</p>
            <Select size={Size.Large} label={t('hr.complaint.about')} value={about} onChange={(value) => void choose(value)} options={[{ value: '', label: t('hr.complaint.aboutNobody') }, ...subjects.map((row) => ({ value: row.employmentId, label: row.name }))]} />
            {route !== null ? (
              <div data-complaint-route="true">
                <p>{t('hr.complaint.routeLead')}</p>
                {route.trim() ? <p className={styles.route}>{route}</p> : <p className={styles.meta}>{t('hr.complaint.routeBlank')}</p>}
                <p className={styles.meta}>{t('hr.complaint.routeKept')}</p>
              </div>
            ) : (
              <Textarea size={Size.Large} label={t('hr.complaint.summary')} value={summary} onChange={setSummary} rows={6} required name="complaintSummary" />
            )}
          </>
        )}
        {message ? <p className={styles.meta} role="alert">{message}</p> : null}
      </div>
    </Modal>
  );
}
